import { useRef, useCallback, useLayoutEffect, useEffect } from 'react';

/**
 * Keeps the minimap viewport indicator in sync with canvas pan/zoom via DOM transforms.
 * Uses refs so parent re-renders during gestures do not snap the viewport incorrectly.
 */
export function useMinimapViewport({ pan, zoom, containerRef, bounds, scale }) {
  const viewportRef = useRef(null);
  const containerSizeRef = useRef({ width: 0, height: 0 });
  const panRef = useRef(pan);
  const zoomRef = useRef(zoom);

  const updateViewport = useCallback(
    (panX, panY, currentZoom) => {
      if (!viewportRef.current || !containerSizeRef.current.width) return;

      const canvasLeft = -panX / currentZoom;
      const canvasTop = -panY / currentZoom;
      const canvasWidth = containerSizeRef.current.width / currentZoom;
      const canvasHeight = containerSizeRef.current.height / currentZoom;

      const viewX = (canvasLeft - bounds.minX) * scale;
      const viewY = (canvasTop - bounds.minY) * scale;
      const viewW = canvasWidth * scale;
      const viewH = canvasHeight * scale;

      const nextW = `${viewW}px`;
      const nextH = `${viewH}px`;
      viewportRef.current.style.transform = `translate(${viewX}px, ${viewY}px)`;
      if (viewportRef.current.style.width !== nextW) viewportRef.current.style.width = nextW;
      if (viewportRef.current.style.height !== nextH) viewportRef.current.style.height = nextH;
    },
    [bounds.minX, bounds.minY, scale],
  );

  useLayoutEffect(() => {
    panRef.current = pan;
    zoomRef.current = zoom;
    if (!viewportRef.current || !containerRef?.current) return;
    const r = containerRef.current.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) {
      containerSizeRef.current = { width: r.width, height: r.height };
    }
    updateViewport(pan.x, pan.y, zoom);
  }, [pan, zoom, containerRef, updateViewport]);

  useEffect(() => {
    const canvas = containerRef?.current?.querySelector('.flowchart__canvas');
    if (!canvas) return;

    const handlePanUpdate = (e) => {
      const { x, y, zoom: currentZoom } = e.detail;
      panRef.current = { x, y };
      zoomRef.current = currentZoom;
      updateViewport(x, y, currentZoom);
    };

    canvas.addEventListener('panupdate', handlePanUpdate);
    return () => canvas.removeEventListener('panupdate', handlePanUpdate);
  }, [containerRef, updateViewport]);

  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return undefined;

    const syncSize = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        containerSizeRef.current = { width: r.width, height: r.height };
        updateViewport(panRef.current.x, panRef.current.y, zoomRef.current);
      }
    };

    syncSize();
    const ro = new ResizeObserver(syncSize);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, updateViewport]);

  return { viewportRef, containerSizeRef, panRef, zoomRef };
}
